import { z } from 'zod';
import { Cents, IsoDate, Uuid } from './common.js';
import { Partner, PartnerKind } from './data.js';

/**
 * Lot W « Champs Axonaut » : canaux d'acquisition dynamiques, tri des fiches Axonaut, autres noms des apporteurs.
 * Routes `GET /api/acquisition/channels`, `GET /api/acquisition/clients` (+ `.csv`), `POST|DELETE
 * /api/partners/:id/aliases`.
 */

/** Canal connu : valeurs effectives distinctes (Axonaut, sinon saisie du Cockpit), libellé tel que tapé dans Axonaut. */
export const AcquisitionChannelOption = z.object({
  /** Clé stable de regroupement (slug ; alias ramenés aux clés connues, ex. `partenaires-webdesign`). */
  key: z.string(),
  label: z.string(),
  /** Clients dont c'est le canal effectif. */
  clients: z.int().min(0),
  /** Au moins une fiche Axonaut porte ce canal. */
  fromAxonaut: z.boolean(),
});
export type AcquisitionChannelOption = z.infer<typeof AcquisitionChannelOption>;

/** Ligne du tableau « Tri des fiches Axonaut » (et de son export CSV). */
export const AxonautSortingRow = z.object({
  clientId: Uuid,
  name: z.string(),
  axonautCompanyId: z.string().nullable(),
  /** Champ « Canal d'acquisition » de la fiche, tel quel. */
  axonautChannel: z.string().nullable(),
  /** Champ « Apporteur d'affaire » de la fiche, tel quel (jamais découpé). */
  axonautApporteur: z.string().nullable(),
  /** Apporteur du Cockpit reconnu pour ce nom (nom ou autre nom), null sinon. */
  axonautApporteurMatch: z.string().nullable(),
  /** Saisie actuelle du Cockpit (client, sinon ses devis) : libellé du canal. */
  cockpitChannel: z.string().nullable(),
  /** Saisie actuelle du Cockpit : nom de l'apporteur. */
  cockpitApporteur: z.string().nullable(),
  /** Devis signés du client (hors devis à 0 €). */
  signedQuotes: z.int().min(0),
  /** Dernière signature (Paris). */
  lastSignedOn: IsoDate.nullable(),
});
export type AxonautSortingRow = z.infer<typeof AxonautSortingRow>;

/** `POST /api/partners/:id/aliases` : « Rattacher à un apporteur existant » (interne, sans plan). */
export const AddPartnerAliasBody = z.object({ name: z.string().trim().min(1).max(200) });
export type AddPartnerAliasBody = z.infer<typeof AddPartnerAliasBody>;

/** Nom du champ Axonaut « Apporteur d'affaire » qu'aucun apporteur du Cockpit ne porte (ou ignoré). */
export const UnknownApporteur = z.object({
  /** Valeur telle que tapée dans Axonaut (la plus fréquente pour ce nom). */
  name: z.string(),
  /** Nom normalisé (clé de rapprochement). */
  nameKey: z.string(),
  /** Fiches qui portent ce nom. */
  clients: z.array(z.object({ id: Uuid, name: z.string(), axonautCompanyId: z.string().nullable() })),
});
export type UnknownApporteur = z.infer<typeof UnknownApporteur>;

/**
 * `GET /api/acquisition/unknown-apporteurs` : liste « Noms d'apporteurs inconnus » (ceux que compte la carte
 * « Apporteurs Axonaut à reconnaître ») et noms ignorés (réversibles), par nom.
 */
export const UnknownApporteursList = z.object({
  unknown: z.array(UnknownApporteur),
  ignored: z.array(UnknownApporteur.extend({ ignoredAt: z.string() })),
});
export type UnknownApporteursList = z.infer<typeof UnknownApporteursList>;

/** `POST /api/acquisition/unknown-apporteurs/ignore` et `…/unignore` (internes, sans plan). */
export const ApporteurNameBody = z.object({ name: z.string().trim().min(1).max(200) });
export type ApporteurNameBody = z.infer<typeof ApporteurNameBody>;

// ------------------------------------------------------------------ Apporteurs (lot BN)

/** Période du tableau « Par apporteur » : année en cours, 12 derniers mois, depuis le début. */
export const PartnerPeriod = z.enum(['year', '12m', 'all']);
export type PartnerPeriod = z.infer<typeof PartnerPeriod>;
export const PartnerPeriodQuery = z.object({ period: PartnerPeriod.default('year') });

/** `GET /api/partners/stats?period=` : une ligne par apporteur (mêmes chiffres que « D'où viennent les clients »). */
export const PartnerStatsRow = z.object({
  partnerId: Uuid,
  name: z.string(),
  kind: PartnerKind,
  r1Clients: z.int().min(0),
  quotedClients: z.int().min(0),
  newClients: z.int().min(0),
  /** Nouveaux clients ÷ R1 ; null sans R1 ou quand les agendas ne couvrent pas la période. */
  conversionRate: z.number().nullable(),
  signedCents: Cents,
  balanceTtcCents: Cents,
});
export type PartnerStatsRow = z.infer<typeof PartnerStatsRow>;

/** D'où vient l'apporteur d'un client (règle d'attribution unique). */
export const ApporteurLinkedBy = z.enum([
  'axonaut',
  'axonaut_alias',
  'cockpit_client',
  'cockpit_quote',
  'r1',
]);
export type ApporteurLinkedBy = z.infer<typeof ApporteurLinkedBy>;

/** `GET /api/partners/:id/detail?period=` : chiffres clés, clients un par un, noms inconnus qui lui ressemblent. */
export const PartnerDetail = z.object({
  partner: Partner,
  period: PartnerPeriod,
  stats: PartnerStatsRow,
  /** Jour depuis lequel les agendas sont lus quand ils ne couvrent pas la période (R1 « depuis le … »), sinon null. */
  r1CoveredFrom: IsoDate.nullable(),
  r1CoverageComplete: z.boolean(),
  clients: z.array(
    z.object({
      /** null : prospect sans fiche Axonaut (infos du R1). */
      clientId: Uuid.nullable(),
      name: z.string(),
      axonautUrl: z.string().nullable(),
      r1On: IsoDate.nullable(),
      quote: z
        .object({
          number: z.string().nullable(),
          date: IsoDate.nullable(),
          amountCents: Cents,
          status: z.enum(['pending', 'accepted', 'refused']),
        })
        .nullable(),
      newClient: z.boolean(),
      linkedBy: ApporteurLinkedBy,
      /** Nom tapé dans Axonaut quand il diffère du nom de l'apporteur (« fiche Axonaut (« S. Courteix ») »). */
      axonautName: z.string().nullable(),
    }),
  ),
  unknownNames: z.array(UnknownApporteur),
});
export type PartnerDetail = z.infer<typeof PartnerDetail>;

export const PartnerAuditKind = z.enum([
  'missing_apporteur',
  'unknown_name',
  'channel_like',
  'suspicious_alias',
  'conflict',
  'no_clients',
]);
export type PartnerAuditKind = z.infer<typeof PartnerAuditKind>;

/** Une ligne de la carte « À vérifier » (un problème). `key` stable, pour « Ignorer ». */
export const PartnerAuditItem = z.object({
  key: z.string(),
  kind: PartnerAuditKind,
  title: z.string(),
  detail: z.string(),
  clientId: Uuid.optional(),
  partnerId: Uuid.optional(),
  aliasId: Uuid.optional(),
  /** Nom concerné (alias suspect, nom inconnu) : bouton « Rattacher », « Retirer cet alias ». */
  name: z.string().optional(),
  axonautUrl: z.string().optional(),
});
export type PartnerAuditItem = z.infer<typeof PartnerAuditItem>;

/** `GET /api/partners/audit?period=` : lignes à vérifier et lignes ignorées (réversibles). */
export const PartnerAudit = z.object({
  items: z.array(PartnerAuditItem),
  dismissed: z.array(PartnerAuditItem),
});
export type PartnerAudit = z.infer<typeof PartnerAudit>;
